/**
 * Catering FAQ — one source for three consumers:
 *   - the "Catering Orders" group on /faq
 *   - the FAQ section on /catering
 *   - the FAQPage JSON-LD on both routes
 *
 * They drifted before: /faq promised a 50% deposit that checkout never took and
 * pointed catering customers at the wrong mailbox. Keep the answers here and
 * the three stay in sync by construction.
 */
import { CATERING_EMAIL } from "@/lib/constants/site"
import { CATERING_ONLINE_LEAD_TIME_HOURS } from "@/lib/data/catering-menu"

export type CateringFaqEntry = { q: string; a: string }

// True whether or not the catering page is live, so both lists below use it.
const HOT_BRUNCH_TRAYS: CateringFaqEntry = {
  q: "Do you cater hot brunch trays?",
  a: `+Bake handles pastry boxes, per-person breakfasts and boxed lunches, cookies, brownies, cupcakes, coffee and the dessert table. Full hot brunch trays come from our sister brand Take Ur Seat, who cater those from the same kitchens.`,
}

export const CATERING_FAQ: CateringFaqEntry[] = [
  {
    q: "How far in advance do I need to place a catering order?",
    a: `Boxes and per-person bundles need ${CATERING_ONLINE_LEAD_TIME_HOURS} hours. A Latte Station or Dessert Table needs at least 7 days, and large events (100+ guests) or custom cakes are worth starting 2–3 weeks out. Standing weekly accounts can arrange day-before ordering — ask us to set one up.`,
  },
  {
    q: "How does payment work?",
    a: `Orders placed online are paid in full at checkout. Events booked through a quote — Latte Station, Dessert Table, large deliveries — take a 50% deposit to hold the date, with the balance due the day before the event.`,
  },
  {
    q: "Do you deliver catering orders?",
    a: `We deliver within 10 miles of any Take Ur Seat location on orders of $150 or more; tell us where on the quote form and we will confirm the fee. Otherwise pick up in Rancho Cucamonga at 8685 Base Line Rd, Suite B, in Ontario, or in Chino Hills.`,
  },
  {
    q: "Can I change or cancel a catering order?",
    a: `Online orders can be changed or cancelled up to ${CATERING_ONLINE_LEAD_TIME_HOURS} hours before pickup for a full refund. Inside that window the food is already in production. For quoted events, the deposit holds your date and is non-refundable within 48 hours of the event.`,
  },
  {
    q: "Can I customize my catering order?",
    a: `Yes. Cookie and croissant boxes are built flavor by flavor on the catering page. For anything else — special flavors, dietary accommodations, custom packaging, a standing weekly order — email ${CATERING_EMAIL} or use the quote form and we will put it together.`,
  },
  {
    q: "Do you offer custom cakes for events?",
    a: `Yes, by consultation. Email ${CATERING_EMAIL} or use the quote form to talk through flavors, tiers, size and design. Custom cakes typically need 7–10 days.`,
  },
  {
    q: "What is the Latte Station service?",
    a: `A barista and an espresso cart at your event, pouring pandan, ube, cookie butter and matcha lattes plus espresso classics, hot or iced, on Local Coffee Roasters beans. Two hours and up to 60 drinks from $750. It needs one standard outlet and about a week's notice.`,
  },
  HOT_BRUNCH_TRAYS,
]

/**
 * What /faq says while the catering page is switched off (Admin → Settings →
 * Store). The full CATERING_FAQ above points at "the catering page" and "the
 * quote form", which only exist when the page is live — so while it is hidden,
 * catering is by request through email and the contact form instead.
 */
export const CATERING_FAQ_BY_REQUEST: CateringFaqEntry[] = [
  {
    q: "Do you offer catering?",
    a: `Yes, by request. Email ${CATERING_EMAIL} or choose "Catering Inquiry" on our contact form with your date, headcount and what you have in mind, and we will follow up with options and pricing.`,
  },
  {
    q: "Do you offer custom cakes for events?",
    a: `Yes, by consultation. Email ${CATERING_EMAIL} or choose "Custom Cake Consultation" on our contact form to talk through flavors, size and design. Custom cakes typically need 7–10 days.`,
  },
  HOT_BRUNCH_TRAYS,
]
